import { IShow, TaskTimeLineType } from "@evennt-tech/api-v2-core";
import { useQuery } from "@tanstack/react-query";
import { RequestKey } from "api/request-key";
import { apiV2 } from "api/firebase";
import useMapTimeline from "hooks/show/stand/task/historyTimeline/useMapTimeline";
import { ReactNode, useEffect, useState } from "react";

type UseTaskTimelineArgs = {
  show: IShow;
  standId: string;
  taskId: string;
  type?: TaskTimeLineType[];
};

type UseTaskTimeline = [ReactNode[], boolean, unknown];

export default ({
  taskId,
  standId,
  type,
  show,
}: UseTaskTimelineArgs): UseTaskTimeline => {
  const mapTimeline = useMapTimeline(show, standId);
  const enabled = Boolean(show?.id && standId && taskId);

  const { data: actions, isLoading, isFetching, error } = useQuery({
    queryKey: [
      type?.length
        ? RequestKey.GetTaskTimelineByType
        : RequestKey.GetTaskTimeline,
      show?.id,
      standId,
      taskId,
      ...(type?.length ? [type] : []),
    ],
    queryFn: async () => {
      try {
        return await apiV2.taskTimeline.getMany({
          showId: show.id,
          taskId,
          standId,
          ...(type?.length && {
            type,
          }),
        });
      } catch (e) {
        console.error(e);
        return [];
      }
    },
    enabled,
    // Always-fresh: the timeline modal refetches on every open, no cache.
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  // Rendered here, not in `queryFn`: the rows carry the viewer's date locale
  // and translations, which can resolve after the fetch, and the query key
  // doesn't change when they do. Tagged with the actions they were built from
  // so a refetch or task switch never shows the previous rows.
  const [mapped, setMapped] = useState<{
    actions: typeof actions;
    nodes: ReactNode[];
  } | null>(null);

  useEffect(() => {
    if (!actions) return undefined;
    let active = true;
    Promise.all(actions.map(mapTimeline))
      .catch((e) => {
        console.error(e);
        return [];
      })
      .then((nodes) => {
        if (active) setMapped({ actions, nodes });
      });
    return () => {
      active = false;
    };
  }, [actions, mapTimeline]);

  const nodes = mapped && mapped.actions === actions ? mapped.nodes : null;
  const isMapping = !!actions && nodes === null;

  return [nodes ?? [], isLoading || isFetching || isMapping, error];
};
